import React, { lazy, Suspense } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
} from "@mui/material";

// Lazy load components
const AboutSection = lazy(() => import("../components/AboutSection"));
const AboutSEO = lazy(() => import("../components/AboutSEO"));

const About = () => {
  // Remove unused theme

  // Optimize styles for better performance
  const styles = {
    mainContainer: {
      background: "rgba(15, 23, 42, 0.9)",
      borderRadius: "8px",
      padding: "2rem",
      // Remove expensive backdrop-filter on initial load
    },
    title: {
      color: "#3b82f6",
      fontWeight: 700,
      // Simplify text shadow
      textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
      fontFamily: "'Poppins', sans-serif",
      fontSize: "2.5rem",
      marginBottom: "1rem",
      // Add GPU acceleration
      transform: "translateZ(0)",
    },
    description: {
      color: "rgba(255, 255, 255, 0.87)",
      fontFamily: "'Roboto', sans-serif",
      fontSize: "1.1rem",
      lineHeight: 1.6,
    },
    gridItem: {
      background: "rgba(30, 41, 59, 0.7)",
      padding: "1.5rem",
      borderRadius: "8px",
      height: "100%",
    },
  };

  return (
    <>
      {/* Load SEO component with Suspense */}
      <Suspense fallback={null}>
        <AboutSEO />
      </Suspense>

      <Container maxWidth="lg" sx={{ py: 4, mt: 2, mb: -6 }}>
        <Box sx={styles.mainContainer}>
          {/* Add loading priority to LCP element */}
          <Typography
            variant="h1"
            component="h1"
            sx={styles.title}
            loading="eager"
          >
            About MovieHub - Your Ultimate Movie Download Destination
          </Typography>

          {/* Optimize main content for LCP */}
          <Typography
            variant="body1"
            component="h2"
            paragraph
            sx={styles.description}
            loading="eager"
          >
            Welcome to MovieHub, your premier destination for downloading the latest movies from Bollywood, South Indian cinema, and Hollywood. We bring you an extensive collection of films featuring your favorite stars like Thalapathy Vijay, Vijay Deverakonda, Rashmika Mandanna, and Samantha Ruth Prabhu.Moviehub is there for you.
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Box sx={styles.gridItem}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: "#3b82f6",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  Latest Releases
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255, 255, 255, 0.87)",
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  Stay updated with the newest releases including blockbusters like Pushpa 2, Animal Movie, and Hanuman Movie. We offer high-quality downloads in various formats suitable for all devices.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={styles.gridItem}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: "#3b82f6",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  South Indian Cinema
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255, 255, 255, 0.87)",
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  Explore the best of Tamil and Telugu cinema with our extensive collection of South Indian movies. From action-packed thrillers to emotional dramas, find all the latest releases with Hindi dubbing.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={styles.gridItem}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: "#3b82f6",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  Movie Categories
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255, 255, 255, 0.87)",
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  Browse through our vast collection of movies across different genres. Whether you're looking for the latest Hollywood blockbusters, Bollywood hits, or South Indian action movies, we've got you covered.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 6 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                color: "#3b82f6",
                fontFamily: "Poppins, sans-serif",
                fontWeight: 500,
              }}
            >
              Why Choose MovieHub?
            </Typography>
            <Box
              sx={{
                background: "rgba(30, 41, 59, 0.7)",
                p: 3,
                borderRadius: 2,
                mt: 2,
              }}
            >
              <Typography
                variant="body1"
                component="div"
                sx={{
                  color: "rgba(255, 255, 255, 0.87)",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                <ul style={{ paddingLeft: "20px", listStyleType: "none" }}>
                  {[
                    "Latest movie releases from Bollywood, Hollywood, and South Indian cinema",
                    "High-quality downloads in multiple formats",
                    "Regular updates with new releases",
                    "Easy search and download process",
                    "Movies from top actors and directors",
                    "Hindi dubbed versions available",
                  ].map((item, index) => (
                    <li
                      key={index}
                      style={{
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          color: "#3b82f6",
                          marginRight: "8px",
                          fontSize: "1.2em",
                        }}
                      >
                        •
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                color: "rgba(255, 255, 255, 0.87)",
                fontFamily: "Roboto, sans-serif",
                fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                maxWidth: "800px",
                mx: "auto",
                textAlign: "justify",
                lineHeight: "1.6",
                letterSpacing: "0.2px",
                wordSpacing: "0.5px",
                mb: 2,
                textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                mt: 3,
              }}
            >
              Moviehub - Latest Bollywood, South Indian, and Hollywood Movies in HD Quality - Watch and Download Now
              MovieHub is constantly updated with the latest releases, ensuring you never miss out on new movies. From upcoming releases to classic favorites, we're your go-to destination for all your movie download needs.
            </Typography>
          </Box>
        </Box>
      </Container>
      <Suspense fallback={<div>Loading...</div>}>
        <AboutSection />
      </Suspense>
    </>
  );
};

// Optimize re-renders
export default React.memo(About);
